<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

import { useNuxtApp } from '#app';
import { useBreakpoint, watch } from '#imports';

const props = defineProps<{ modelValue: boolean }>();

const emit = defineEmits<{
    (e: 'update:modelValue', payload: boolean): void;
}>();

const TIME_ANIMATION = 0.21;
const EASE_ANIMATION = 'power1.out';
const BREAK_POINT_MAIN = 768;

const { '$gsap': gsap } = useNuxtApp();
const mm = ref<gsap.MatchMedia>();

const refFilterContainer = ref<HTMLElement | null>(null);
const refFilter = ref<HTMLElement | null>(null);
let filterTimeline: GSAPTimeline | null = null;
const refXsollaMetaframeContainer = ref<HTMLElement | null>(null);

const setBodyStyle = (hidden: boolean) => {
    if (hidden) document.body.classList.add('overflow-hidden');
    else document.body.classList.remove('overflow-hidden');
};

const { isMobileOrTablet } = useDevice();
const { isMobile } = useBreakpoint();

function createAnimation() {
    if (refFilter.value && refFilterContainer.value) {
        mm.value = gsap.matchMedia(refFilter.value);
        mm.value.add(
            `(min-width: ${BREAK_POINT_MAIN}px)`,
            () => {
                emit(
                    'update:modelValue',
                    true
                );
                setBodyStyle(false);
                filterTimeline = gsap
                    .timeline({
                        defaults: {
                            duration: TIME_ANIMATION,
                            ease: EASE_ANIMATION
                        }
                    })
                    .to(
                        refFilterContainer.value,
                        {
                            alpha: 0
                        }
                    )
                    .to(
                        refFilter.value,
                        {
                            translateX: '-100%',
                            width: 0,
                            height: 0,
                            minWidth: 'auto',
                            alpha: 0,
                            marginRight: 0
                        },
                        '<'
                    )
                    .reverse();
            }
        );

        mm.value.add(
            `(max-width: ${BREAK_POINT_MAIN - 1}px)`,
            () => {
                emit(
                    'update:modelValue',
                    true
                );
                filterTimeline = gsap
                    .timeline({
                        defaults: {
                            duration: TIME_ANIMATION,
                            ease: EASE_ANIMATION,
                            onComplete: () => setBodyStyle(true),
                            onReverseComplete: () => setBodyStyle(false)
                        }
                    })
                    .to(
                        refFilterContainer.value,
                        {
                            alpha: 1
                        }
                    )
                    .to(
                        refFilter.value,
                        {
                            translateY: '0%',
                            height: '100vh',
                            alpha: 1
                        },
                        '<'
                    )
                    .reverse();
            }
        );
    }
}

function handleClickOutside(event: MouseEvent) {
    if (refFilterContainer.value && refFilter.value) {
        const targetNode = event.target as Node;
        if (!refFilter.value.contains(targetNode) && !refFilterContainer.value.contains(targetNode)) {
            emit(
                'update:modelValue',
                true
            );
        }
    }
}

const toggleFilter = (direction: boolean) => {
    if (isMobile.value && refXsollaMetaframeContainer.value) {
        refXsollaMetaframeContainer.value.style.display = direction ? 'block' : 'none';
    }
    filterTimeline?.reversed(direction);
};

watch(
    () => props.modelValue,
    (newValue) => {
        toggleFilter(newValue);
    }
);

onMounted(() => {
    createAnimation();
    if (isMobileOrTablet) {
        document.addEventListener(
            'click',
            handleClickOutside
        );
    }
    refXsollaMetaframeContainer.value = document.getElementById('__xsolla_metaframe_container') as HTMLElement | null;
});

onUnmounted(() => {
    mm.value?.revert();
    filterTimeline?.kill();
    refFilter.value = null;
    filterTimeline = null;
    if (isMobileOrTablet) {
        document.removeEventListener(
            'click',
            handleClickOutside
        );
    }
});
</script>

<template>
  <div ref="refFilter"
       class="display-flex gap-8 container-width flex-column modal-style-768"
       v-bind="$attrs"
  >
    <div ref="refFilterContainer"
         class="filters-container-768"
    >
      <div class="display-flex justify-between align-items-center mb-8 filter-header">
        <h3>Filters</h3>
        <button aria-label="Close toast"
                class="cursor-pointer display-flex align-items-center justify-center close-icon__btn"
                type="button"
                @click.stop="emit('update:modelValue', true)"
        >
          <IconTypes name="icon/close"
                     :size="20"
          />
        </button>
      </div>
      <slot></slot>
    </div>
  </div>
</template>

<style lang="scss">
.modal-style-768 {
  margin-right: 35px;

  @include tablet-768 {
    position: fixed;
    z-index: 200;
    top: 0;
    left: 0;
    width: 100%;
    height: 0;
    transform: translateY(-100%);
    opacity: 0;
    background-color: var(--layer-floor-1);
  }
}

.filters-container-768 {
  display: flex;
  flex-direction: column;
  gap: 8px;

  @include tablet-768 {
    display: flex;
    flex-direction: column;
    padding: 12px;
    overflow-y: auto;
    opacity: 0;
    background: var(--layer-floor-1);
    gap: 8px;
  }
}

.filters-heading-768 {
  display: none;

  @include tablet-768 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
  }
}

.container-width {
  width: 100%;
  min-width: 300px;
  max-width: 436px;

  @include tablet-768 {
    width: 100vw;
    max-width: 100%;
  }

  @include mobile-576 {
    min-width: initial;
  }
}

.pad-24 {
  padding: 24px;
}

.pad-8 {
  padding: 8px;
}

.blocks {
  width: 100%;
  border-radius: var(--background-500-radius, 4px);
  background: var(--layer-floor-1);

  @include tablet-768 {
    background: var(--layer-floor-2);
  }
}

.bg-color {
  width: 100%;
  max-width: 80px;
  height: var(--control-400-size-single, 36px);
  border-radius: var(--control-400-radius, 2px);
  background: rgb(233 234 236 / 10%);
  gap: 6px;
}

.rotate-arrow {
  rotate: 270deg;
}
</style>

<style lang="scss" scoped>
.close-icon__btn {
  border-radius: 50%;
  background-color: var(--content-primary);

  :deep(.fill-current) {
    color: var(--layer-floor-1);
  }
}

.filter-header {
  display: none;

  @include tablet-768 {
    display: flex;
  }
}
</style>
